import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import React from "react";
import { Helmet } from "react-helmet";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { Breadcrumb, BreadcrumbParentPage } from "./Breadcrumb";

interface PageTitleProps {
  pageTitle: string;
  pageHeader: any;
  systemOrProjectHeader?: string;
  pageSubHeader?: string;
  children?: any;
  isDataProject?: boolean;
  isSyncPage?: boolean;
  isEnrichPage?: boolean;
  isMonitorPage?: boolean;
  integratedSystemId?: number;
  dataProjectId?: number;
  projectsLinkOverride?: string;
  systemLinkOverride?: string;
  parentPages?: BreadcrumbParentPage[];
  zeroPadding?: boolean;
}

/**
 * PageTitle Component
 *
 * A component to display the page title, header, and optional subheader, along with an optional
 * breadcrumb-like navigation for a system or project. It also sets the page title for the document.
 *
 * @param {string} props.pageTitle - The title of the page to set as the document title.
 * @param {string} props.pageHeader - The main header of the page.
 * @param {string} [props.systemOrProjectHeader] - The header for the system or project, used as part of the breadcrumb.
 * @param {string} [props.pageSubHeader] - An optional subheader for the page.
 * @param {any} [props.children] - Optional children elements to be rendered within the component.
 * @param {boolean} [props.isDataProject] - A flag indicating whether the page represents a data project.
 * @param {number} [props.integratedSystemId] - The ID of the integrated system associated with the page (used in breadcrumb navigation).
 * @param {number} [props.dataProjectId] - The ID of the data project (used in breadcrumb navigation).
 * @param {string} [props.systemLinkOverride] - Overrides the link on the system name in the breadcrumb.
 * @param {BreadcrumbParentPage[]} [props.parentPages] - Intermediate pages between the system and this page, each rendered as a link in the breadcrumb.
 *
 * @returns {JSX.Element} - The rendered PageTitle component.
 */
export const PageTitle = React.memo(function PageTitle(
  props: Readonly<PageTitleProps>
) {
  const {
    children,
    pageTitle,
    pageHeader,
    systemOrProjectHeader,
    pageSubHeader,
    isDataProject,
    isSyncPage,
    isEnrichPage,
    isMonitorPage,
    integratedSystemId,
    dataProjectId,
    projectsLinkOverride,
    systemLinkOverride,
    parentPages,
    zeroPadding,
  } = props;

  return (
    <>
      {/* Set the page title for the document */}
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Box
        style={{
          backgroundColor: "background.default",
        }}
      >
        <Container maxWidth={false} style={zeroPadding ? { padding: 0 } : {}}>
          {/* Breadcrumb navigation when a system or project header is provided */}
          {systemOrProjectHeader && (
            <Breadcrumb
              pageHeader={pageHeader}
              systemOrProjectHeader={systemOrProjectHeader}
              isDataProject={isDataProject}
              isSyncPage={isSyncPage}
              isEnrichPage={isEnrichPage}
              isMonitorPage={isMonitorPage}
              integratedSystemId={integratedSystemId}
              dataProjectId={dataProjectId}
              projectsLinkOverride={projectsLinkOverride}
              systemLinkOverride={systemLinkOverride}
              parentPages={parentPages}
            />
          )}
          {/* Display only the page header if there's no system or project header */}
          {pageHeader && systemOrProjectHeader === undefined && (
            <Typography
              color="textPrimary"
              variant="h3"
              component="h1"
              style={{ margin: "1em 0", marginBottom: 0 }}
            >
              {pageHeader}
            </Typography>
          )}
          {/* Optional subheader. `subtitle1` maps to <h6> by default, which
              dropped an out-of-sequence heading between the page title and the
              first section; it is supporting text, not structure. */}
          {pageSubHeader && (
            <Typography variant="subtitle1" component="p">
              {pageSubHeader}
            </Typography>
          )}

          {/* Divider between title and content */}
          <Divider style={{ margin: "5px 0" }} />
        </Container>
        {/* Render any additional children elements passed to the component */}
        {children}
      </Box>
    </>
  );
});
